import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './BatchPipelineTool.module.css';

/* ────────── constants ────────── */
const EDGE_TTS_PREFIX = '/edge-tts';
const TRANSLATE_MODEL = 'gpt-5-nano';
const NOTES_MODEL = 'gpt-5-nano';
const TRANSLATE_CHUNK_SIZE = 20;
const TRANSLATE_CONCURRENCY = 100;
const POLL_MS = 3000;

const VOICE_CATEGORIES = [
  { value: '', label: '全部' },
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'multilingual', label: '多语言' },
];

const STEP_KEYS = ['translate', 'notes', 'tts', 'combine'];
const STEP_LABELS = {
  translate: '翻译 (OpenAI)',
  notes: 'AI 笔记',
  tts: '语音生成 (Edge TTS)',
  combine: 'OSS 混音',
};

const initialStepState = () => ({
  status: 'idle', // idle | running | done | failed | skipped
  progress: 0,
  detail: '',
});

/* ────────── helpers ────────── */
function ts() {
  return new Date().toLocaleTimeString('zh-CN', { hour12: false });
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/* ────────── component ────────── */
function BatchPipelineTool() {
  /* ── volumes / chapters ── */
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [selectedVolumeId, setSelectedVolumeId] = useState('');

  /* ── voices (英文 + 中文分别选择) ── */
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [selectedEnVoice, setSelectedEnVoice] = useState(null);
  const [selectedCnVoice, setSelectedCnVoice] = useState(null);
  const [enVoiceFilter, setEnVoiceFilter] = useState('en');
  const [cnVoiceFilter, setCnVoiceFilter] = useState('zh');

  /* ── pipeline state ── */
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState(() =>
    Object.fromEntries(STEP_KEYS.map((k) => [k, initialStepState()])),
  );
  const [logs, setLogs] = useState([]);
  const [globalError, setGlobalError] = useState('');
  const cancelledRef = useRef(false);
  const logEndRef = useRef(null);

  /* ── derived ── */
  const enFilteredVoices = useMemo(() => {
    if (!enVoiceFilter) return voices;
    return voices.filter((v) => v.category === enVoiceFilter);
  }, [voices, enVoiceFilter]);

  const cnFilteredVoices = useMemo(() => {
    if (!cnVoiceFilter) return voices;
    return voices.filter((v) => v.category === cnVoiceFilter);
  }, [voices, cnVoiceFilter]);

  const selectedVolume = useMemo(
    () => volumes.find((v) => String(v.id) === String(selectedVolumeId)) || null,
    [volumes, selectedVolumeId],
  );

  const chaptersInVolume = useMemo(() => {
    if (!selectedVolume || !Array.isArray(selectedVolume.chapters)) return [];
    return [...selectedVolume.chapters].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
  }, [selectedVolume]);

  const chapterIds = useMemo(
    () => chaptersInVolume.map((c) => c.id),
    [chaptersInVolume],
  );

  /* ── log helper ── */
  const addLog = useCallback((text, type = 'info') => {
    setLogs((prev) => [...prev, { time: ts(), text, type }]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  /* ── load data ── */
  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    try {
      const { data } = await api.get('/volumes/', {
        params: { limit: 500, include_chapters: true },
      });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((v) => String(v.id) === String(prev))) return prev;
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch {
      addLog('获取卷列表失败', 'error');
    } finally {
      setVolumesLoading(false);
    }
  }, [addLog]);

  const loadVoices = useCallback(async () => {
    setVoicesLoading(true);
    try {
      const { data } = await api.get(`${EDGE_TTS_PREFIX}/voices/all`);
      const list = Array.isArray(data?.voices) ? data.voices : [];
      setVoices(list);
      // 自动选择：英文音色 → 必须来自 en/multilingual；中文音色 → 必须来自 zh/multilingual
      setSelectedEnVoice((prev) => {
        if (prev && prev.category !== 'zh' && list.some((v) => v.shortName === prev.shortName)) return prev;
        return list.find((v) => v.category === 'en') || list.find((v) => v.category === 'multilingual') || null;
      });
      setSelectedCnVoice((prev) => {
        if (prev && prev.category !== 'en' && list.some((v) => v.shortName === prev.shortName)) return prev;
        return list.find((v) => v.category === 'zh') || list.find((v) => v.category === 'multilingual') || null;
      });
    } catch {
      addLog('获取声音列表失败', 'error');
    } finally {
      setVoicesLoading(false);
    }
  }, [addLog]);

  useEffect(() => {
    loadVolumes();
    loadVoices();
  }, [loadVolumes, loadVoices]);

  /* ── step helpers ── */
  const updateStep = useCallback((key, patch) => {
    setSteps((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const resetSteps = useCallback(() => {
    setSteps(Object.fromEntries(STEP_KEYS.map((k) => [k, initialStepState()])));
  }, []);

  /* ────────────────────────────────────────────────
     Step 1: Translation (async polling)
  ──────────────────────────────────────────────── */
  const runTranslation = useCallback(
    async (ids) => {
      updateStep('translate', { status: 'running', progress: 0, detail: '正在启动翻译任务…' });
      addLog(`[翻译] 开始翻译 ${ids.length} 个章节`);

      const payload = {
        chapter_ids: ids,
        target_language: 'English',
        model: TRANSLATE_MODEL,
        chunk_size: TRANSLATE_CHUNK_SIZE,
        max_concurrent_requests: TRANSLATE_CONCURRENCY,
      };

      const { data } = await api.post('/openai-translate/batch', payload, { timeout: 60000 });
      if (!data?.task_id) throw new Error('未能启动翻译任务');

      const taskId = data.task_id;
      addLog(`[翻译] 任务 ${taskId.slice(0, 8)} 已启动，轮询中…`);

      // poll with retry tolerance
      let consecutiveErrors = 0;
      const MAX_POLL_ERRORS = 5;

      while (true) {
        if (cancelledRef.current) throw new Error('用户取消');
        await sleep(POLL_MS);

        try {
          const { data: s } = await api.get(`/openai-translate/batch/status/${taskId}`, { timeout: 30000 });
          consecutiveErrors = 0; // reset on success

          const pct = s.progress || 0;
          const detail = `${s.translated_sentences ?? 0}/${s.total_sentences ?? '?'} 句 — ${s.current_chapter_title || ''}`;
          updateStep('translate', { progress: pct, detail });

          if (s.status === 'completed') {
            addLog(`[翻译] 全部完成，成功 ${s.succeeded_chapters}/${s.total_chapters} 章`, 'success');
            updateStep('translate', { status: 'done', progress: 100, detail: `${s.succeeded_chapters}/${s.total_chapters} 章完成` });
            return;
          }
          if (s.status === 'failed') {
            throw new Error(s.error || '翻译失败');
          }
        } catch (pollErr) {
          // 如果是后端明确返回的失败，直接抛出
          if (pollErr.message && !pollErr.response) throw pollErr;

          consecutiveErrors += 1;
          addLog(`[翻译] 轮询出错 (${consecutiveErrors}/${MAX_POLL_ERRORS})：${pollErr?.response?.status || pollErr.message}`, 'error');

          if (consecutiveErrors >= MAX_POLL_ERRORS) {
            throw new Error(`轮询连续失败 ${MAX_POLL_ERRORS} 次，放弃等待（后端任务可能仍在运行）`);
          }
          // 继续重试
        }
      }
    },
    [addLog, updateStep],
  );

  /* ────────────────────────────────────────────────
     Step 2: AI Notes (sync, long wait)
  ──────────────────────────────────────────────── */
  const runNotes = useCallback(
    async (ids) => {
      updateStep('notes', { status: 'running', progress: 0, detail: '正在批量生成笔记…' });
      addLog(`[笔记] 开始为 ${ids.length} 个章节生成 AI 笔记`);

      const { data } = await api.post(
        '/ai-notes/batch',
        { chapter_ids: ids, model: NOTES_MODEL },
        { timeout: 0 },
      );

      const succeeded = data.succeeded ?? 0;
      const total = data.total_requested ?? ids.length;
      addLog(`[笔记] 完成，成功 ${succeeded}/${total} 章`, succeeded === total ? 'success' : 'error');
      updateStep('notes', {
        status: succeeded > 0 ? 'done' : 'failed',
        progress: 100,
        detail: `${succeeded}/${total} 章完成`,
      });

      if (succeeded === 0) throw new Error('所有章节笔记生成失败');
    },
    [addLog, updateStep],
  );

  /* ────────────────────────────────────────────────
     Step 3: TTS  (per-chapter, en + cn)
  ──────────────────────────────────────────────── */
  const pollTtsTask = useCallback(async (taskId) => {
    while (true) {
      if (cancelledRef.current) throw new Error('用户取消');
      await sleep(POLL_MS);
      const { data } = await api.get(`${EDGE_TTS_PREFIX}/tts/${taskId}`);
      if (data.status === 'completed') return data;
      if (data.status === 'failed') throw new Error(data.error || 'TTS 失败');
    }
  }, []);

  const runTts = useCallback(
    async (chapters, enVoiceName, cnVoiceName, needModesMap) => {
      updateStep('tts', { status: 'running', progress: 0, detail: '准备中…' });

      const voiceMap = { en: enVoiceName, cn: cnVoiceName };

      // 计算实际需要的任务数
      let totalTasks = 0;
      for (const chapter of chapters) {
        const modes = needModesMap?.get(chapter.id) || ['en', 'cn'];
        totalTasks += modes.length;
      }

      addLog(`[语音] 开始处理 ${chapters.length} 个章节，共 ${totalTasks} 个语音任务`);
      addLog(`[语音] 英文音色: ${enVoiceName} | 中文音色: ${cnVoiceName}`);

      let finished = 0;

      for (const chapter of chapters) {
        if (cancelledRef.current) throw new Error('用户取消');

        const modes = needModesMap?.get(chapter.id) || ['en', 'cn'];

        for (const mode of modes) {
          if (cancelledRef.current) throw new Error('用户取消');

          const voice = voiceMap[mode];
          const modeLabel = mode === 'en' ? '英文(content_trans)' : '中文(content)';
          const label = `章节 ${chapter.id} (${chapter.title || ''}) ${modeLabel} → ${voice}`;
          updateStep('tts', {
            detail: label,
            progress: Math.round((finished / totalTasks) * 100),
          });
          addLog(`[语音] 生成 ${label}`);

          try {
            const { data } = await api.post(`${EDGE_TTS_PREFIX}/tts/chapter`, {
              chapter_id: chapter.id,
              mode,
              voice,
            });

            if (data?.task_id) {
              await pollTtsTask(data.task_id);
            }
            addLog(`[语音] ${label} ✓`, 'success');
          } catch (err) {
            const msg = err?.response?.data?.detail || err.message || '';
            if (msg.includes('缺少') || msg.includes('content')) {
              addLog(`[语音] ${label} 跳过：${msg}`);
            } else {
              addLog(`[语音] ${label} 失败：${msg}`, 'error');
            }
          }

          finished += 1;
        }
      }

      updateStep('tts', { status: 'done', progress: 100, detail: `${chapters.length} 章完成` });
      addLog(`[语音] 全部完成`, 'success');
    },
    [addLog, updateStep, pollTtsTask],
  );

  /* ────────────────────────────────────────────────
     Step 4: OSS Combine (sync, per-chapter)
  ──────────────────────────────────────────────── */
  const runCombine = useCallback(
    async (chapters) => {
      updateStep('combine', { status: 'running', progress: 0, detail: '准备中…' });
      addLog(`[混音] 开始为 ${chapters.length} 个章节混合音频`);

      let finished = 0;
      let failCount = 0;

      for (const chapter of chapters) {
        if (cancelledRef.current) throw new Error('用户取消');

        const label = `章节 ${chapter.id} (${chapter.title || ''})`;
        updateStep('combine', {
          detail: label,
          progress: Math.round((finished / chapters.length) * 100),
        });

        try {
          await api.post('/oss-mp3/combine', {
            chapter_id: chapter.id,
            oss_prefix: `audios/${chapter.id}`,
            overwrite: false,
          });
          addLog(`[混音] ${label} ✓`, 'success');
        } catch (err) {
          const msg = err?.response?.data?.detail || err.message || '';
          addLog(`[混音] ${label} 失败：${msg}`, 'error');
          failCount += 1;
        }

        finished += 1;
      }

      const successCount = chapters.length - failCount;
      updateStep('combine', {
        status: failCount === chapters.length ? 'failed' : 'done',
        progress: 100,
        detail: `${successCount}/${chapters.length} 章完成`,
      });
      addLog(`[混音] 全部完成，成功 ${successCount}/${chapters.length}`, failCount === 0 ? 'success' : 'error');
    },
    [addLog, updateStep],
  );

  /* ────────── 预检查：获取最新状态 ────────── */
  const fetchFreshChapters = useCallback(async (volumeId) => {
    const { data } = await api.get('/volumes/', {
      params: { limit: 500, include_chapters: true },
    });
    const list = Array.isArray(data) ? data : [];
    setVolumes(list);
    const vol = list.find((v) => String(v.id) === String(volumeId));
    if (!vol || !Array.isArray(vol.chapters)) return [];
    return [...vol.chapters].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, []);

  const fetchOssStatus = useCallback(async (chapterIdsList) => {
    // 返回 Map<chapterId, { existing: Set, hasRequired: bool }>
    const ossMap = new Map();
    try {
      const { data } = await api.get('/oss-mp3/chapters', { params: { prefix: 'audios', limit: 1000 } });
      const items = Array.isArray(data?.chapters) ? data.chapters : [];
      for (const item of items) {
        const name = item.name || item.prefix?.split('/').pop() || '';
        const cid = Number.parseInt(name, 10);
        if (!Number.isFinite(cid)) continue;
        if (!chapterIdsList.includes(cid)) continue;
        // 优先使用 found_files（后端返回的目录下所有文件名）
        const existing = Array.isArray(item.found_files) && item.found_files.length > 0
          ? new Set(item.found_files)
          : (() => {
              // fallback: 从 missing_files 反推
              const missing = new Set(Array.isArray(item.missing_files) ? item.missing_files : []);
              const allRequired = ['en.mp3', 'en.srt', 'cn.mp3', 'cn.srt'];
              return new Set(allRequired.filter((f) => !missing.has(f)));
            })();
        ossMap.set(cid, { existing, hasRequired: Boolean(item.has_required_files) });
      }
    } catch {
      // OSS 检查失败不阻塞，默认都需要处理
    }
    return ossMap;
  }, []);

  /* ────────── pipeline ────────── */
  const startPipeline = useCallback(async () => {
    if (!selectedEnVoice || !selectedCnVoice) {
      setGlobalError('请先选择英文和中文语音音色');
      return;
    }
    // 校验音色是否匹配语言
    if (selectedEnVoice.category === 'zh') {
      setGlobalError(`英文音色选择了中文声音 (${selectedEnVoice.shortName})，请重新选择`);
      return;
    }
    if (selectedCnVoice.category === 'en') {
      setGlobalError(`中文音色选择了英文声音 (${selectedCnVoice.shortName})，请重新选择`);
      return;
    }
    if (chapterIds.length === 0) {
      setGlobalError('所选卷没有章节');
      return;
    }

    setGlobalError('');
    resetSteps();
    setLogs([]);
    setRunning(true);
    cancelledRef.current = false;

    const volLabel = selectedVolume?.novel?.title
      ? `${selectedVolume.novel.title} — ${selectedVolume.title}`
      : selectedVolume?.title || selectedVolumeId;

    addLog(`===== 流水线启动 =====`);
    addLog(`卷: ${volLabel} | ${chapterIds.length} 章`);
    addLog(`英文音色: ${selectedEnVoice.shortName} (${selectedEnVoice.category}) | 中文音色: ${selectedCnVoice.shortName} (${selectedCnVoice.category})`);

    /* ── Step 0: 预检查数据库和 OSS 状态 ── */
    addLog(`[预检查] 正在获取章节最新状态…`);
    let freshChapters;
    try {
      freshChapters = await fetchFreshChapters(selectedVolumeId);
    } catch {
      freshChapters = chaptersInVolume;
      addLog(`[预检查] 获取最新数据失败，使用缓存数据`, 'error');
    }

    const freshIds = freshChapters.map((c) => c.id);

    // 检查翻译状态
    const needTranslateIds = freshChapters.filter((c) => !c.has_content_trans).map((c) => c.id);
    const needNotesIds = freshChapters.filter((c) => !c.has_ai_notes).map((c) => c.id);

    addLog(`[预检查] 翻译：${freshIds.length - needTranslateIds.length}/${freshIds.length} 已完成，待处理 ${needTranslateIds.length} 章`);
    addLog(`[预检查] 笔记：${freshIds.length - needNotesIds.length}/${freshIds.length} 已完成，待处理 ${needNotesIds.length} 章`);

    // 检查 OSS 音频状态
    let ossMap;
    try {
      ossMap = await fetchOssStatus(freshIds);
    } catch {
      ossMap = new Map();
    }

    const needTtsChapters = [];
    const needTtsModes = new Map(); // chapterId -> ['en', 'cn'] 需要生成的 mode
    for (const ch of freshChapters) {
      const oss = ossMap.get(ch.id);
      const modes = [];
      if (!oss || !oss.existing.has('en.mp3')) modes.push('en');
      if (!oss || !oss.existing.has('cn.mp3')) modes.push('cn');
      if (modes.length > 0) {
        needTtsChapters.push(ch);
        needTtsModes.set(ch.id, modes);
      }
    }
    addLog(`[预检查] 语音：${freshIds.length - needTtsChapters.length}/${freshIds.length} 已完成，待处理 ${needTtsChapters.length} 章`);

    const needCombineChapters = freshChapters.filter((ch) => {
      const oss = ossMap.get(ch.id);
      if (!oss?.hasRequired) return false; // 基础4文件不齐全，无法 combine
      // 已同时存在 en_cn.mp3 和 cn_en.mp3 则跳过
      if (oss.existing.has('en_cn.mp3') && oss.existing.has('cn_en.mp3')) return false;
      return true;
    });
    const alreadyCombined = freshChapters.filter((ch) => {
      const oss = ossMap.get(ch.id);
      return oss?.existing?.has('en_cn.mp3') && oss?.existing?.has('cn_en.mp3');
    }).length;
    addLog(`[预检查] 混音：${alreadyCombined}/${freshIds.length} 已完成，待处理 ${needCombineChapters.length} 章`);

    /* ── 执行各步骤 ── */
    const pipelineSteps = [
      {
        key: 'translate',
        fn: needTranslateIds.length > 0
          ? () => runTranslation(needTranslateIds)
          : null,
        skipReason: needTranslateIds.length === 0 ? `全部 ${freshIds.length} 章已翻译` : null,
      },
      {
        key: 'notes',
        fn: needNotesIds.length > 0
          ? () => runNotes(needNotesIds)
          : null,
        skipReason: needNotesIds.length === 0 ? `全部 ${freshIds.length} 章已有笔记` : null,
      },
      {
        key: 'tts',
        fn: needTtsChapters.length > 0
          ? () => runTts(needTtsChapters, selectedEnVoice.shortName, selectedCnVoice.shortName, needTtsModes)
          : null,
        skipReason: needTtsChapters.length === 0 ? `全部 ${freshIds.length} 章音频已生成` : null,
      },
      {
        key: 'combine',
        fn: needCombineChapters.length > 0
          ? () => runCombine(needCombineChapters)
          : null,
        skipReason: needCombineChapters.length === 0 ? '全部章节混音已完成（或缺少基础音频）' : null,
      },
    ];

    for (const step of pipelineSteps) {
      if (cancelledRef.current) {
        updateStep(step.key, { status: 'skipped', detail: '已取消' });
        continue;
      }
      if (!step.fn) {
        updateStep(step.key, { status: 'done', progress: 100, detail: step.skipReason });
        addLog(`[${STEP_LABELS[step.key]}] 跳过 — ${step.skipReason}`, 'success');
        continue;
      }
      try {
        await step.fn();
      } catch (err) {
        const msg = err.message || '未知错误';
        updateStep(step.key, { status: 'failed', detail: msg });
        addLog(`[${STEP_LABELS[step.key]}] 失败: ${msg}，继续执行后续步骤…`, 'error');
      }
    }

    setRunning(false);
    addLog(`===== 流水线结束 =====`, 'success');
  }, [
    selectedEnVoice,
    selectedCnVoice,
    chapterIds,
    chaptersInVolume,
    selectedVolume,
    selectedVolumeId,
    resetSteps,
    addLog,
    updateStep,
    fetchFreshChapters,
    fetchOssStatus,
    runTranslation,
    runNotes,
    runTts,
    runCombine,
  ]);

  const handleStop = useCallback(() => {
    cancelledRef.current = true;
    addLog('用户请求停止…', 'error');
  }, [addLog]);

  /* ────────────────────────────────────────────────
     语音二次检查 & 补生成
  ──────────────────────────────────────────────── */
  const [recheckResult, setRecheckResult] = useState(null); // [{id, title, hasEn, hasCn}]
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [retryRunning, setRetryRunning] = useState(false);

  // 清空检查结果 when volume changes
  useEffect(() => {
    setRecheckResult(null);
  }, [selectedVolumeId]);

  const handleRecheck = useCallback(async () => {
    if (chaptersInVolume.length === 0) return;
    setRecheckLoading(true);
    setRecheckResult(null);
    addLog('[语音检查] 正在检查 OSS 文件…');

    try {
      const ids = chaptersInVolume.map((c) => c.id);
      const ossMap = await fetchOssStatus(ids);

      const result = chaptersInVolume.map((ch) => {
        const oss = ossMap.get(ch.id);
        return {
          id: ch.id,
          title: ch.title || `#${ch.id}`,
          hasEn: Boolean(oss?.existing?.has('en.mp3')),
          hasCn: Boolean(oss?.existing?.has('cn.mp3')),
          hasEnCn: Boolean(oss?.existing?.has('en_cn.mp3')),
          hasCnEn: Boolean(oss?.existing?.has('cn_en.mp3')),
        };
      });

      setRecheckResult(result);

      const missingEn = result.filter((r) => !r.hasEn).length;
      const missingCn = result.filter((r) => !r.hasCn).length;
      const missingEnCn = result.filter((r) => !r.hasEnCn).length;
      const missingCnEn = result.filter((r) => !r.hasCnEn).length;
      addLog(
        `[语音检查] 完成：${result.length} 章，缺少 en.mp3: ${missingEn}，缺少 cn.mp3: ${missingCn}，缺少 en_cn.mp3: ${missingEnCn}，缺少 cn_en.mp3: ${missingCnEn}`,
        missingEn + missingCn + missingEnCn + missingCnEn === 0 ? 'success' : 'error',
      );
    } catch (err) {
      addLog(`[语音检查] 失败：${err.message || '未知错误'}`, 'error');
    } finally {
      setRecheckLoading(false);
    }
  }, [chaptersInVolume, fetchOssStatus, addLog]);

  const handleRetryMissing = useCallback(async () => {
    if (!recheckResult || !selectedEnVoice || !selectedCnVoice) return;

    const tasks = [];
    for (const r of recheckResult) {
      const chapter = chaptersInVolume.find((c) => c.id === r.id);
      if (!chapter) continue;
      if (!r.hasEn) tasks.push({ chapter, mode: 'en' });
      if (!r.hasCn) tasks.push({ chapter, mode: 'cn' });
    }
    if (tasks.length === 0) {
      addLog('[补生成] 无缺失项，无需操作', 'success');
      return;
    }

    setRetryRunning(true);
    cancelledRef.current = false;
    const voiceMap = { en: selectedEnVoice.shortName, cn: selectedCnVoice.shortName };
    const MAX_RETRIES = 3;
    let succeeded = 0;
    let failed = 0;

    addLog(`[补生成] 开始处理 ${tasks.length} 个缺失任务…`);

    for (const { chapter, mode } of tasks) {
      if (cancelledRef.current) {
        addLog('[补生成] 用户取消', 'error');
        break;
      }

      const voice = voiceMap[mode];
      const modeLabel = mode === 'en' ? '英文' : '中文';
      const label = `${chapter.title || chapter.id} [${modeLabel}] → ${voice}`;

      let ok = false;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          addLog(`[补生成] ${label}（第 ${attempt} 次尝试）…`);
          const { data } = await api.post(`${EDGE_TTS_PREFIX}/tts/chapter`, {
            chapter_id: chapter.id,
            mode,
            voice,
          });
          if (data?.task_id) {
            await pollTtsTask(data.task_id);
          }
          addLog(`[补生成] ${label} ✓`, 'success');
          ok = true;
          break;
        } catch (err) {
          const msg = err?.response?.data?.detail || err.message || '';
          addLog(`[补生成] ${label} 第 ${attempt} 次失败：${msg}`, 'error');
          if (attempt < MAX_RETRIES) {
            const waitSec = attempt * 10;
            addLog(`[补生成] 等待 ${waitSec}s 后重试…`);
            await sleep(waitSec * 1000);
          }
        }
      }
      if (ok) {
        succeeded += 1;
      } else {
        failed += 1;
      }
    }

    addLog(`[补生成] 完成：成功 ${succeeded}，失败 ${failed}`, failed === 0 ? 'success' : 'error');
    setRetryRunning(false);

    // 自动重新检查
    if (succeeded > 0) {
      addLog('[补生成] 自动重新检查…');
      // 延迟执行避免 OSS 缓存
      await sleep(2000);
      setRecheckLoading(true);
      try {
        const ids = chaptersInVolume.map((c) => c.id);
        const ossMap = await fetchOssStatus(ids);
        const result = chaptersInVolume.map((ch) => {
          const oss = ossMap.get(ch.id);
          return {
            id: ch.id,
            title: ch.title || `#${ch.id}`,
            hasEn: Boolean(oss?.existing?.has('en.mp3')),
            hasCn: Boolean(oss?.existing?.has('cn.mp3')),
            hasEnCn: Boolean(oss?.existing?.has('en_cn.mp3')),
            hasCnEn: Boolean(oss?.existing?.has('cn_en.mp3')),
          };
        });
        setRecheckResult(result);
        const stillMissing = result.filter((r) => !r.hasEn || !r.hasCn || !r.hasEnCn || !r.hasCnEn).length;
        addLog(
          `[语音检查] 更新完成，仍有 ${stillMissing} 章缺失`,
          stillMissing === 0 ? 'success' : 'error',
        );
      } catch {
        addLog('[语音检查] 自动检查失败', 'error');
      } finally {
        setRecheckLoading(false);
      }
    }
  }, [recheckResult, chaptersInVolume, selectedEnVoice, selectedCnVoice, addLog, pollTtsTask, fetchOssStatus]);

  /* ── 补生成缺失混音 ── */
  const handleRetryMissingCombine = useCallback(async () => {
    if (!recheckResult) return;

    const tasks = recheckResult.filter(
      (r) => r.hasEn && r.hasCn && (!r.hasEnCn || !r.hasCnEn),
    );
    if (tasks.length === 0) {
      addLog('[补混音] 无缺失项，无需操作', 'success');
      return;
    }

    setRetryRunning(true);
    cancelledRef.current = false;
    let succeeded = 0;
    let failed = 0;

    addLog(`[补混音] 开始处理 ${tasks.length} 个章节…`);

    for (const r of tasks) {
      if (cancelledRef.current) {
        addLog('[补混音] 用户取消', 'error');
        break;
      }

      const label = `章节 ${r.id} (${r.title})`;
      try {
        addLog(`[补混音] ${label}…`);
        await api.post('/oss-mp3/combine', {
          chapter_id: r.id,
          oss_prefix: `audios/${r.id}`,
          overwrite: false,
        });
        addLog(`[补混音] ${label} ✓`, 'success');
        succeeded += 1;
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || '';
        addLog(`[补混音] ${label} 失败：${msg}`, 'error');
        failed += 1;
      }
    }

    addLog(`[补混音] 完成：成功 ${succeeded}，失败 ${failed}`, failed === 0 ? 'success' : 'error');
    setRetryRunning(false);

    // 自动重新检查
    if (succeeded > 0) {
      addLog('[补混音] 自动重新检查…');
      await sleep(2000);
      setRecheckLoading(true);
      try {
        const ids = chaptersInVolume.map((c) => c.id);
        const ossMap = await fetchOssStatus(ids);
        const result = chaptersInVolume.map((ch) => {
          const oss = ossMap.get(ch.id);
          return {
            id: ch.id,
            title: ch.title || `#${ch.id}`,
            hasEn: Boolean(oss?.existing?.has('en.mp3')),
            hasCn: Boolean(oss?.existing?.has('cn.mp3')),
            hasEnCn: Boolean(oss?.existing?.has('en_cn.mp3')),
            hasCnEn: Boolean(oss?.existing?.has('cn_en.mp3')),
          };
        });
        setRecheckResult(result);
        const stillMissing = result.filter((r) => !r.hasEn || !r.hasCn || !r.hasEnCn || !r.hasCnEn).length;
        addLog(
          `[语音检查] 更新完成，仍有 ${stillMissing} 章缺失`,
          stillMissing === 0 ? 'success' : 'error',
        );
      } catch {
        addLog('[语音检查] 自动检查失败', 'error');
      } finally {
        setRecheckLoading(false);
      }
    }
  }, [recheckResult, chaptersInVolume, addLog, fetchOssStatus]);

  /* ── voice label ── */
  const renderVoiceLabel = (voice) => {
    const name = voice.localName || voice.displayName || voice.shortName;
    const parts = [name];
    if (voice.gender) {
      parts.push(`[${voice.gender === 'Female' ? '女' : voice.gender === 'Male' ? '男' : voice.gender}]`);
    }
    parts.push(`| ${voice.locale}`);
    if (voice.category === 'multilingual') parts.push('| 多语言');
    return parts.join(' ');
  };

  /* ── render step dot ── */
  const dotClass = (status) => {
    switch (status) {
      case 'running': return styles.stepDotRunning;
      case 'done': return styles.stepDotDone;
      case 'failed': return styles.stepDotFailed;
      case 'skipped': return styles.stepDotSkipped;
      default: return styles.stepDotIdle;
    }
  };

  const dotIcon = (status, idx) => {
    switch (status) {
      case 'done': return '✓';
      case 'failed': return '✕';
      case 'skipped': return '—';
      default: return idx + 1;
    }
  };

  const statusText = (status) => {
    switch (status) {
      case 'idle': return '等待';
      case 'running': return '进行中';
      case 'done': return '完成';
      case 'failed': return '失败';
      case 'skipped': return '跳过';
      default: return '';
    }
  };

  const statusClass = (status) => {
    switch (status) {
      case 'running': return styles.stepStatusRunning;
      case 'done': return styles.stepStatusDone;
      case 'failed': return styles.stepStatusFailed;
      default: return '';
    }
  };

  /* ────────── render ────────── */
  return (
    <div className={styles.pipeline}>
      {/* ── Config ── */}
      <div className={styles.configCard}>
        <h2 className={styles.configTitle}>一键处理流水线</h2>
        <p className={styles.configHint}>
          选择卷和语音音色后，自动依次执行：翻译 → AI 笔记 → 语音合成 → 混音
        </p>

        {/* Volume */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="pipeVol">
            选择卷
            {chaptersInVolume.length > 0 && (
              <span className={styles.labelHint}>共 {chaptersInVolume.length} 章</span>
            )}
          </label>
          <select
            id="pipeVol"
            className={styles.select}
            value={selectedVolumeId}
            onChange={(e) => setSelectedVolumeId(e.target.value)}
            disabled={volumesLoading || running}
          >
            {volumes.length === 0 && <option value="">暂无数据</option>}
            {volumes.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {v.novel?.title ? `${v.novel.title} — ${v.title}` : v.title || `卷 ${v.id}`}
              </option>
            ))}
          </select>
        </div>

        {/* English Voice */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="pipeEnVoice">
            英文音色
            {selectedEnVoice && (
              <span className={styles.labelHint}>{selectedEnVoice.shortName}</span>
            )}
          </label>
          <div className={styles.voiceFilterRow}>
            {VOICE_CATEGORIES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.filterChip} ${enVoiceFilter === opt.value ? styles.filterChipActive : ''}`}
                onClick={() => {
                  setEnVoiceFilter(opt.value);
                  const next = opt.value ? voices.filter((v) => v.category === opt.value) : voices;
                  if (selectedEnVoice && !next.some((v) => v.shortName === selectedEnVoice.shortName)) {
                    setSelectedEnVoice(next.length > 0 ? next[0] : null);
                  }
                }}
                disabled={running}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <select
            id="pipeEnVoice"
            className={styles.select}
            value={selectedEnVoice?.shortName || ''}
            onChange={(e) => {
              const v = enFilteredVoices.find((x) => x.shortName === e.target.value);
              setSelectedEnVoice(v || null);
            }}
            disabled={voicesLoading || enFilteredVoices.length === 0 || running}
          >
            {enFilteredVoices.length === 0 && <option value="">暂无声音</option>}
            {enFilteredVoices.map((v) => (
              <option key={v.shortName} value={v.shortName}>
                {renderVoiceLabel(v)}
              </option>
            ))}
          </select>
        </div>

        {/* Chinese Voice */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="pipeCnVoice">
            中文音色
            {selectedCnVoice && (
              <span className={styles.labelHint}>{selectedCnVoice.shortName}</span>
            )}
          </label>
          <div className={styles.voiceFilterRow}>
            {VOICE_CATEGORIES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.filterChip} ${cnVoiceFilter === opt.value ? styles.filterChipActive : ''}`}
                onClick={() => {
                  setCnVoiceFilter(opt.value);
                  const next = opt.value ? voices.filter((v) => v.category === opt.value) : voices;
                  if (selectedCnVoice && !next.some((v) => v.shortName === selectedCnVoice.shortName)) {
                    setSelectedCnVoice(next.length > 0 ? next[0] : null);
                  }
                }}
                disabled={running}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <select
            id="pipeCnVoice"
            className={styles.select}
            value={selectedCnVoice?.shortName || ''}
            onChange={(e) => {
              const v = cnFilteredVoices.find((x) => x.shortName === e.target.value);
              setSelectedCnVoice(v || null);
            }}
            disabled={voicesLoading || cnFilteredVoices.length === 0 || running}
          >
            {cnFilteredVoices.length === 0 && <option value="">暂无声音</option>}
            {cnFilteredVoices.map((v) => (
              <option key={v.shortName} value={v.shortName}>
                {renderVoiceLabel(v)}
              </option>
            ))}
          </select>
        </div>

        {/* Chapter summary */}
        {chaptersInVolume.length > 0 && (
          <div className={styles.chapterSummary}>
            <span>章节：{chaptersInVolume.map((c) => c.title || `#${c.id}`).join('、')}</span>
          </div>
        )}

        {globalError && <div className={styles.errorMessage}>{globalError}</div>}

        {/* Actions */}
        <div className={styles.actionRow}>
          {!running && !retryRunning ? (
            <>
              <button
                type="button"
                className={styles.startBtn}
                disabled={volumesLoading || voicesLoading || chapterIds.length === 0 || !selectedEnVoice || !selectedCnVoice}
                onClick={startPipeline}
              >
                开始处理
              </button>
              <button
                type="button"
                className={styles.recheckBtn}
                disabled={volumesLoading || chapterIds.length === 0 || recheckLoading}
                onClick={handleRecheck}
              >
                {recheckLoading ? '检查中…' : '检查语音'}
              </button>
            </>
          ) : (
            <button type="button" className={styles.stopBtn} onClick={handleStop}>
              停止
            </button>
          )}
        </div>
      </div>

      {/* ── Recheck Result ── */}
      {recheckResult && (
        <div className={styles.recheckCard}>
          <div className={styles.recheckHeader}>
            <h3 className={styles.recheckTitle}>
              语音检查结果
              <span className={styles.recheckSummary}>
                {recheckResult.filter((r) => !r.hasEn || !r.hasCn || !r.hasEnCn || !r.hasCnEn).length === 0
                  ? '全部完整 ✓'
                  : `${recheckResult.filter((r) => !r.hasEn || !r.hasCn || !r.hasEnCn || !r.hasCnEn).length} 章缺失`}
              </span>
            </h3>
            {recheckResult.some((r) => !r.hasEn || !r.hasCn) && (
              <button
                type="button"
                className={styles.retryBtn}
                disabled={retryRunning || running || !selectedEnVoice || !selectedCnVoice}
                onClick={handleRetryMissing}
              >
                {retryRunning ? '生成中…' : '补生成缺失语音'}
              </button>
            )}
            {recheckResult.some((r) => r.hasEn && r.hasCn && (!r.hasEnCn || !r.hasCnEn)) && (
              <button
                type="button"
                className={styles.retryBtn}
                disabled={retryRunning || running}
                onClick={handleRetryMissingCombine}
              >
                {retryRunning ? '生成中…' : '补生成缺失混音'}
              </button>
            )}
          </div>
          <div className={styles.recheckTable}>
            <div className={`${styles.recheckRow} ${styles.recheckRowHead}`}>
              <span className={styles.recheckCellId}>#</span>
              <span className={styles.recheckCellTitle}>章节</span>
              <span className={styles.recheckCellStatus}>en.mp3</span>
              <span className={styles.recheckCellStatus}>cn.mp3</span>
              <span className={styles.recheckCellStatus}>en_cn</span>
              <span className={styles.recheckCellStatus}>cn_en</span>
            </div>
            {recheckResult.map((r) => (
              <div
                key={r.id}
                className={`${styles.recheckRow} ${(!r.hasEn || !r.hasCn || !r.hasEnCn || !r.hasCnEn) ? styles.recheckRowMissing : ''}`}
              >
                <span className={styles.recheckCellId}>{r.id}</span>
                <span className={styles.recheckCellTitle}>{r.title}</span>
                <span className={`${styles.recheckCellStatus} ${r.hasEn ? styles.statusOk : styles.statusMiss}`}>
                  {r.hasEn ? '✓' : '✕'}
                </span>
                <span className={`${styles.recheckCellStatus} ${r.hasCn ? styles.statusOk : styles.statusMiss}`}>
                  {r.hasCn ? '✓' : '✕'}
                </span>
                <span className={`${styles.recheckCellStatus} ${r.hasEnCn ? styles.statusOk : styles.statusMiss}`}>
                  {r.hasEnCn ? '✓' : '✕'}
                </span>
                <span className={`${styles.recheckCellStatus} ${r.hasCnEn ? styles.statusOk : styles.statusMiss}`}>
                  {r.hasCnEn ? '✓' : '✕'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Steps ── */}
      <div className={styles.stepsCard}>
        <h3 className={styles.stepsTitle}>流水线进度</h3>
        {STEP_KEYS.map((key, idx) => {
          const st = steps[key];
          return (
            <div key={key} className={styles.stepItem}>
              {/* indicator */}
              <div className={styles.stepIndicatorCol}>
                <div className={`${styles.stepDot} ${dotClass(st.status)}`}>
                  {dotIcon(st.status, idx)}
                </div>
                {idx < STEP_KEYS.length - 1 && (
                  <div className={`${styles.stepLine} ${st.status === 'done' ? styles.stepLineDone : ''}`} />
                )}
              </div>

              {/* content */}
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepName}>{STEP_LABELS[key]}</span>
                  <span className={`${styles.stepStatusText} ${statusClass(st.status)}`}>
                    {statusText(st.status)}
                  </span>
                </div>

                {st.detail && <div className={styles.stepDetail}>{st.detail}</div>}

                {(st.status === 'running' || st.status === 'done') && (
                  <div className={styles.progressBarWrapper}>
                    <div
                      className={`${styles.progressBarFill} ${st.status === 'done' ? styles.progressBarFillDone : ''}`}
                      style={{ width: `${st.progress}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Logs ── */}
      <div className={styles.logCard}>
        <div className={styles.logHeader}>
          <h3 className={styles.logTitle}>日志</h3>
          {logs.length > 0 && (
            <button type="button" className={styles.logClearBtn} onClick={() => setLogs([])}>
              清空
            </button>
          )}
        </div>
        <div className={styles.logBody}>
          {logs.length === 0 && <div style={{ color: 'var(--gray-40)' }}>暂无日志</div>}
          {logs.map((entry, i) => (
            <div key={i} className={styles.logEntry}>
              <span className={styles.logTime}>{entry.time}</span>
              <span
                className={`${styles.logText} ${
                  entry.type === 'error' ? styles.logError : entry.type === 'success' ? styles.logSuccess : ''
                }`}
              >
                {entry.text}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export default BatchPipelineTool;

